// src/hooks/useOnboardingState.js
// Reusable hook — reads onboarding state from the DB.
// Never reads from localStorage. Always queries the server.
//
// Returns: { state, loading, error }
//   state   — OnboardingState object or null
//   loading — true while the DB query is in flight
//   error   — true if the query threw (network error, RLS block, etc.)
//
// Re-fetches automatically whenever userId changes (login / account switch).
// Cleans up via mounted flag — no setState after unmount.

import { useState, useEffect } from "react";
import { getOnboardingState } from "../lib/onboarding/onboardingService";
import { hasSupabase } from "../lib/supabaseClient";

export function useOnboardingState(userId) {
  const [state,   setState]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  useEffect(() => {
    // No user yet — hold in loading until userId arrives
    if (!userId) {
      setState(null);
      setLoading(true);
      setError(false);
      return;
    }

    // Supabase unavailable (local dev without .env) — fall back to localStorage
    // so the hook never blocks the app in offline / no-config environments.
    if (!hasSupabase) {
      const done = localStorage.getItem("onboarding_complete");
      setState(done ? { status: "complete", current_step: 0, steps_completed: [] } : { status: "pending", current_step: 0, steps_completed: [] });
      setLoading(false);
      setError(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(false);

    getOnboardingState(userId)
      .then(data => {
        if (!mounted) return;
        setState(data);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setError(true);
        setLoading(false);
      });

    // Cleanup — prevents setState calls after the component using this hook unmounts
    return () => { mounted = false; };
  }, [userId]);

  return { state, loading, error };
}
